(() => {
  'use strict';
  if (window.mpEntregaCart) return;
  const panels = new Set();
  let mode, desired, saved, saving, currentPanel, restoring, restoreError;
  let revision = 0;
  const signature = attributes => JSON.stringify(attributes);
  const status = (text, retry = false) => panels.forEach(panel => {
    panel.querySelector('.mp-entrega-status').hidden = !text;
    panel.querySelector('[data-mp-save-status]').textContent = text;
    panel.querySelector('[data-mp-retry]').hidden = !retry;
  });
  const syncForms = attributes => {
    document.querySelectorAll('.cart-page, cart-drawer form.cart-drawer__footer').forEach(form => {
      Object.entries(attributes).forEach(([name, value]) => {
        let input = form.querySelector(`input[name="attributes[${name}]"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden'; input.name = `attributes[${name}]`; form.append(input);
        }
        input.value = value;
      });
    });
  };
  const paintAll = () => panels.forEach(panel => panel.paint());
  const fromServer = attributes => {
    revision++;
    restoreError = false;
    mode = attributes.mp_entrega_modo === 'junto' ? 'junto' : 'separado';
    desired = { mp_entrega_modo: attributes.mp_entrega_modo || '', mp_entrega_fechas: attributes.mp_entrega_fechas || '' };
    saved = signature(desired);
    syncForms(desired);
    panels.forEach(panel => panel.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = input.value === mode; }));
    paintAll();
  };
  const flush = async () => {
    if (saving) { await saving; return flush(); }
    if (restoring || restoreError || !desired || signature(desired) === saved) return;
    saving = (async () => {
      while (!restoring && !restoreError && desired && signature(desired) !== saved) {
        const attributes = { ...desired };
        const sentRevision = revision;
        status('Guardando tu preferencia…');
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes }),
        });
        if (!response.ok) throw new Error('No se ha podido guardar la entrega');
        const cart = await response.json();
        if (Object.entries(attributes).some(([key, value]) => (cart.attributes?.[key] || '') !== value)) throw new Error('Entrega no confirmada');
        // Un refresco de sección invalida la preferencia anterior, incluso con un POST en vuelo.
        saved = sentRevision === revision ? signature(attributes) : undefined;
      }
      status('');
    })().catch(error => {
      status('No se ha podido guardar tu preferencia de entrega.', true);
      throw error;
    }).finally(() => { saving = undefined; });
    return saving;
  };
  const restore = () => {
    if (restoring) return restoring;
    revision++;
    desired = undefined;
    restoreError = false;
    panels.forEach(panel => panel.querySelectorAll('input[type="radio"]').forEach(input => { input.disabled = true; }));
    restoring = (async () => {
      // Esperar las escrituras ya enviadas antes de leer la verdad del servidor.
      if (saving) await saving.catch(() => {});
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Carrito no disponible');
      fromServer((await response.json()).attributes || {});
    })().catch(error => {
      restoreError = true;
      status('No se ha podido actualizar tu preferencia de entrega.', true);
      throw error;
    }).finally(() => {
      restoring = undefined;
      panels.forEach(panel => panel.querySelectorAll('input[type="radio"]').forEach(input => { input.disabled = false; }));
    });
    return restoring;
  };
  window.addEventListener('pageshow', event => {
    if (event.persisted) restore().then(() => { paintAll(); return flush(); }).catch(() => {});
  });
  const save = async () => {
    if (restoreError) await restore();
    if (restoring) await restoring;
    await Promise.all([...panels].map(panel => panel.ready));
    // También actualiza las fechas si se alcanza la hora de corte antes de pagar.
    paintAll();
    return flush();
  };
  class CartEntregas extends HTMLElement {
    connectedCallback() {
      panels.add(this);
      this.data = JSON.parse(this.querySelector('[data-mp-delivery-data]').textContent);
      this.allocateLines();
      currentPanel = this;
      fromServer({ mp_entrega_modo: this.data.mode, mp_entrega_fechas: this.data.dates });
      this.onChange = event => {
        if (!event.target.matches('input[type="radio"]') || restoring || restoreError) return;
        mode = event.target.value;
        paintAll();
        flush().catch(() => {});
      };
      this.addEventListener('change', this.onChange);
      this.querySelector('[data-mp-retry]').addEventListener('click', () => window.mpEntregaCart.save().catch(() => {}));
      this.ready = this.initialize();
      this.timer = setInterval(() => {
        if (this !== currentPanel || restoring || restoreError) return;
        this.paint();
        flush().catch(() => {});
      }, 60000);
    }
    allocateLines() {
      const variants = new Map();
      this.data.lines.forEach(line => {
        if (!line.stock || !line.shipping) return;
        const key = line.variantId ?? line.key;
        const inventory = Math.max(0, Math.floor(Number(line.inventory) || 0));
        const entry = variants.get(key) || { quantity: 0, inventory };
        entry.quantity += line.quantity;
        entry.inventory = Math.min(entry.inventory, inventory);
        variants.set(key, entry);
      });
      const remaining = new Map([...variants].map(([key, value]) => [key, Math.min(value.quantity, value.inventory)]));
      this.lines = this.data.lines.map(line => {
        const key = line.variantId ?? line.key;
        const stockQuantity = line.stock && line.shipping ? Math.min(line.quantity, remaining.get(key)) : 0;
        if (line.stock && line.shipping) remaining.set(key, remaining.get(key) - stockQuantity);
        return { ...line, stockQuantity, missingQuantity: line.stock && line.shipping ? line.quantity - stockQuantity : 0 };
      });
    }
    async initialize() {
      try {
        this.holidays = await window.mpEntrega.loadHolidays(this.dataset.holidaysUrl);
        if (!this.isConnected) return;
        this.placeLines();
        paintAll();
        await flush();
      } catch (error) {
        // Sin calendario, conserva el carrito original y permite reintentar antes de pagar.
        if (!this.holidays && this.isConnected) {
          this.hidden = false;
          status('No se han podido calcular las fechas de entrega.', true);
        }
        this.loadError = !this.holidays;
      }
    }
    placeLines() {
      if (!this.data.lines.some(line => line.shipping)) return;
      this.root = this.dataset.context === 'drawer' ? this.closest('cart-drawer') : this.closest('form');
      const original = this.root.querySelector(this.dataset.context === 'drawer' ? '.cart-drawer__items' : '.order-summary');
      if (!original || original.closest('mp-cart-entregas')) return;
      const nodes = this.dataset.context === 'drawer' ? [...original.children] : [...original.querySelectorAll('tbody>tr')];
      // La metadata y los controles proceden del mismo render de Liquid: mantener su orden.
      this.lines.forEach((line, index) => {
        if (!nodes[index]) return;
        const kind = line.stockQuantity > 0 ? 'stock' : 'pedido';
        const group = this.querySelector(`[data-mp-group="${kind}"] [data-mp-group-lines]`);
        if (line.missingQuantity > 0) {
          const note = document.createElement('p');
          note.className = 'mp-entrega-allocation';
          note.textContent = line.stockQuantity > 0
            ? `${line.stockQuantity} de ${line.quantity} cajas · desde nuestro almacén. Las otras ${line.missingQuantity} cajas van en la entrega 2; cantidad total del carrito: ${line.quantity} cajas.`
            : `${line.missingQuantity} cajas · sin stock en almacén: te confirmaremos la fecha`;
          (nodes[index].querySelector('.line-item-info') || nodes[index]).prepend(note);
          if (line.stockQuantity > 0) {
            // Resumen informativo del excedente: ningún control ni custom element duplicado.
            const summary = document.createElement('div');
            summary.className = 'mp-entrega-overflow';
            const title = document.createElement('p'); title.textContent = line.title;
            const detail = document.createElement('p');
            detail.textContent = `${line.missingQuantity} cajas · sin stock en almacén: te confirmaremos la fecha`;
            summary.append(title, detail);
            this.querySelector('[data-mp-group="pedido"] [data-mp-group-lines]').append(summary);
          }
        }
        if (this.dataset.context === 'page') {
          let table = group.querySelector('table');
          if (!table) {
            table = document.createElement('table'); table.className = original.className;
            const head = original.querySelector('thead');
            if (head) table.append(head.cloneNode(true));
            // Conservar las clases responsive del tema (ocultan columnas duplicadas en móvil).
            const body = original.querySelector('tbody');
            table.append(body ? body.cloneNode(false) : document.createElement('tbody')); group.append(table);
          }
          table.querySelector('tbody').append(nodes[index]);
        } else group.append(nodes[index]);
      });
      original.hidden = true;
      original.style.display = 'none';
    }
    paint() {
      if (!this.holidays || !this.isConnected) return;
      const shipping = this.lines.filter(line => line.shipping);
      const stockLines = shipping.filter(line => line.stockQuantity > 0);
      const orderLines = shipping.filter(line => !line.stock);
      const missingQuantity = shipping.reduce((total, line) => total + line.missingQuantity, 0);
      const hasMissing = missingQuantity > 0;
      const hasStock = stockLines.length > 0, hasOrder = orderLines.length > 0 || hasMissing;
      const mixed = hasStock && hasOrder;
      const selectedMode = mixed ? mode : 'separado';
      const now = new Date();
      const stock = window.mpEntrega.window({ now, holidays: this.holidays });
      const order = orderLines.length ? window.mpEntrega.latest(orderLines.map(line => {
        const lead = window.mpEntrega.parseLeadDays(line.lead);
        return window.mpEntrega.window({ now, minDays: lead.min, maxDays: lead.max, holidays: this.holidays });
      })) : stock;
      const together = window.mpEntrega.latest([stock, order]);
      const confirmation = `${missingQuantity} cajas · sin stock en almacén: te confirmaremos la fecha`;
      const orderDates = [orderLines.length ? `${hasMissing ? 'Bajo pedido: ' : ''}${window.mpEntrega.range(order)}` : '', hasMissing ? confirmation : ''].filter(Boolean).join('. ');
      const togetherDates = hasMissing ? 'te confirmaremos la fecha' : window.mpEntrega.range(together);
      const orderTitle = hasMissing ? (orderLines.length ? 'Bajo pedido y sin stock en almacén' : 'Sin stock en almacén') : 'Bajo pedido';
      this.hidden = !shipping.length;
      this.querySelector('[data-mp-delivery-intro]').textContent = (mixed && selectedMode !== 'junto' ? 'Tu pedido llega en 2 entregas.' : 'Tu pedido llega en una entrega.')
        + (hasMissing ? ' Hay cajas sin stock en almacén: te confirmaremos la fecha.' : '');
      [['stock', hasStock, 'Desde nuestro almacén', window.mpEntrega.range(stock)], ['pedido', hasOrder, orderTitle, orderDates]].forEach(([kind, visible, title, dates], index) => {
        const group = this.querySelector(`[data-mp-group="${kind}"]`);
        // Las líneas sin envío (p. ej. una tarjeta regalo) conservan sus controles.
        group.hidden = !visible && !group.querySelector('line-item');
        group.querySelector('[data-mp-group-title]').textContent = mixed ? `Entrega ${index + 1} · ${title}` : title;
        group.querySelector('[data-mp-group-dates]').textContent = visible ? (selectedMode === 'junto' ? togetherDates : dates) : '';
      });
      this.querySelector('fieldset').hidden = !mixed;
      this.querySelector('[data-mp-together]').textContent = `Todo junto, ${togetherDates}`;
      this.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = input.value === selectedMode; });
      if (this === currentPanel && !restoring && !restoreError) {
        const dates = !shipping.length ? '' : mixed && selectedMode === 'junto'
          ? `Todo junto, ${togetherDates}`
          : mixed ? `Entrega 1 · Desde nuestro almacén: ${window.mpEntrega.range(stock)}. Entrega 2 · ${orderTitle}: ${orderDates}`
            : `${hasStock ? 'Desde nuestro almacén' : orderTitle}: ${hasStock ? window.mpEntrega.range(stock) : orderDates}`;
        desired = { mp_entrega_modo: shipping.length ? selectedMode : '', mp_entrega_fechas: dates };
        syncForms(desired);
      }
    }
    disconnectedCallback() {
      panels.delete(this);
      if (currentPanel === this) currentPanel = [...panels].at(-1);
      clearInterval(this.timer);
      this.removeEventListener('change', this.onChange);
    }
  }
  window.mpEntregaCart = { save: async () => {
    for (const panel of panels) {
      if (panel.loadError) { panel.loadError = false; panel.ready = panel.initialize(); }
    }
    await save();
    if ([...panels].some(panel => panel.loadError)) throw new Error('Calendario no disponible');
  } };
  customElements.define('mp-cart-entregas', CartEntregas);
})();
